import { z } from 'zod';

import { skillProficiencyLevelSchema, skillTypeSchema } from './skill';

const opaqueIdSchema = z.string().trim().min(1);
const nonEmptyTextSchema = z.string().trim().min(1);

export const profileTaskGenerationStateTransportSchema = z.union([
    z.literal('queued'),
    z.literal('started'),
    z.literal('finished'),
    z.literal('failed'),
]);

export type ProfileTaskGenerationStateTransport = z.infer<typeof profileTaskGenerationStateTransportSchema>;

export const profileTaskClassificationValueTransportSchema = z.union([
    z.literal('contribution'),
    z.literal('commodity'),
]);

export type ProfileTaskClassificationValueTransport = z.infer<typeof profileTaskClassificationValueTransportSchema>;

export const profileTaskClassificationTransportSchema = z.object({
    reasoning: z.string().optional(),
    value: profileTaskClassificationValueTransportSchema,
});

export type ProfileTaskClassificationTransport = z.infer<typeof profileTaskClassificationTransportSchema>;

export const profileTaskEstimatedEffortTransportSchema = z
    .object({
        maximumHoursPerWeek: z.number().positive().max(168),
        minimumHoursPerWeek: z.number().positive().max(168),
        reasoning: z.string().optional(),
    })
    .refine(({ maximumHoursPerWeek, minimumHoursPerWeek }) => minimumHoursPerWeek <= maximumHoursPerWeek, {
        message: 'Minimum estimated effort must not exceed maximum estimated effort',
    });

export type ProfileTaskEstimatedEffortTransport = z.infer<typeof profileTaskEstimatedEffortTransportSchema>;

export const profileTaskPhysicalityTransportSchema = z.union([z.literal('physical'), z.literal('non-physical')]);

export const profileTaskAssessmentStateTransportSchema = z.union([
    z.literal('queued'),
    z.literal('started'),
    z.literal('finished'),
    z.literal('failed'),
]);

export type ProfileTaskAssessmentStateTransport = z.infer<typeof profileTaskAssessmentStateTransportSchema>;

export const automationRecommendationStateTransportSchema = z.enum(['pending', 'succeeded', 'failed']);

export type AutomationRecommendationStateTransport = z.infer<typeof automationRecommendationStateTransportSchema>;

/** Absent when no recommendation was ever requested; an unknown state hides the recommendation. */
export const automationRecommendationStateFieldSchema = automationRecommendationStateTransportSchema
    .optional()
    .catch(undefined);

export const profileTaskStepCapabilityTransportSchema = z.enum([
    'NOT_WRITABLE',
    'NEEDS_CONTEXT',
    'ROUTINE',
    'FULLY_SPECIFIED',
]);

export type ProfileTaskStepCapabilityTransport = z.infer<typeof profileTaskStepCapabilityTransportSchema>;

export const profileTaskStepErrorCostTransportSchema = z.enum([
    'IRREVERSIBLE',
    'COSTLY',
    'NOTICEABLE',
    'MINOR',
    'MACHINE_CHECKED',
    'TRIVIAL',
]);

export type ProfileTaskStepErrorCostTransport = z.infer<typeof profileTaskStepErrorCostTransportSchema>;

/** How hard the task's typical error hits one business driver, from not at all to critically. */
const profileTaskImpactLevelTransportSchema = z.enum(['NONE', 'MINOR', 'NOTICEABLE', 'SIGNIFICANT', 'CRITICAL']);

/** The five business drivers the assessment rates for a task's typical error. */
export const profileTaskImpactDriversTransportSchema = z.object({
    compliance: profileTaskImpactLevelTransportSchema,
    cost: profileTaskImpactLevelTransportSchema,
    peopleServed: profileTaskImpactLevelTransportSchema,
    revenue: profileTaskImpactLevelTransportSchema,
    risk: profileTaskImpactLevelTransportSchema,
});

const shareSchema = z.number().int().min(0).max(100);
const fractionSchema = z.number().min(0).max(1);

/** One rated workflow step of the backend's step-based automation assessment, ordered by stepIndex. */
export const profileTaskStepAssessmentTransportSchema = z.object({
    capability: profileTaskStepCapabilityTransportSchema,
    coordinationShare: shareSchema,
    coreInteractionShare: shareSchema,
    digitalShare: shareSchema,
    effortShare: shareSchema,
    errorCost: profileTaskStepErrorCostTransportSchema,
    mandatedHumanInvolvement: z.boolean(),
    normalizedEffortShare: fractionSchema,
    rationale: z.string(),
    score: fractionSchema,
    stepIndex: z.number().int().nonnegative(),
    workflowStepTitle: z.string(),
});

export type ProfileTaskStepAssessmentTransport = z.infer<typeof profileTaskStepAssessmentTransportSchema>;

/**
 * The backend's automation assessment of the pinned task version: the derived task scores plus,
 * on the profile read, one rating per workflow step. Scores are 0-1 fractions.
 */
export const profileTaskAssessmentTransportSchema = z
    .object({
        // Absent on assessments the model did not rate for business impact, older backends included. A level
        // this client does not know yet reads as no rating rather than failing the assessment.
        businessImpactDrivers: profileTaskImpactDriversTransportSchema.optional().catch(undefined),
        // The task's matrix value, 0 to 1, combined from the drivers.
        businessImpact: fractionSchema.optional(),
        businessImpactRationale: z.string().optional(),
        horizon: z.string(),
        levers: z.string().array().default([]),
        methodVersion: z.string(),
        origin: z.string(),
        // Step indexes key the workflow steps the ratings belong to, so a duplicate would silently
        // show one step's rating on another.
        steps: profileTaskStepAssessmentTransportSchema
            .array()
            .refine((steps) => new Set(steps.map(({ stepIndex }) => stepIndex)).size === steps.length, {
                message: 'Assessment step indexes must be unique',
            })
            .optional(),
        summary: z.string(),
        taskScore: fractionSchema,
        taskScoreLower: fractionSchema,
        taskScoreUpper: fractionSchema,
    })
    .refine(
        ({ taskScore, taskScoreLower, taskScoreUpper }) => taskScoreLower <= taskScore && taskScore <= taskScoreUpper,
        { message: 'Task score must fall within its score range' },
    );

export type ProfileTaskAssessmentTransport = z.infer<typeof profileTaskAssessmentTransportSchema>;

export const profileTaskWorkflowStepTransportSchema = z.object({
    // Per-step classification arrives with PRO-2108; older backends omit it.
    classification: profileTaskClassificationTransportSchema.optional(),
    description: z.string().optional(),
    physicality: profileTaskPhysicalityTransportSchema.optional(),
    title: nonEmptyTextSchema,
});

export const profileTaskWorkflowTransportSchema = z.object({
    steps: profileTaskWorkflowStepTransportSchema.array().min(1),
});

export type ProfileTaskWorkflowTransport = z.infer<typeof profileTaskWorkflowTransportSchema>;

/**
 * One language's task body on the pinned version. Entries are complete for their language once
 * generation finished; while it is still running they carry titles only.
 */
export const profileTaskTranslationTransportSchema = z.object({
    classificationReasoning: z.string().optional(),
    description: z.string().optional(),
    estimatedEffortReasoning: z.string().optional(),
    language: z.string(),
    title: z.string(),
    workflow: profileTaskWorkflowTransportSchema.optional(),
});

export type ProfileTaskTranslationTransport = z.infer<typeof profileTaskTranslationTransportSchema>;

export const profileTaskSkillTitleTransportSchema = z.object({
    language: z.string(),
    title: z.string(),
});

export const profileTaskRequiredSkillTransportSchema = z.object({
    id: opaqueIdSchema,
    // Detected proficiency on the skill-type scale (hard/soft 0-5, language 0-6).
    level: skillProficiencyLevelSchema.optional(),
    order: z.number().int().nonnegative().default(0),
    skillType: skillTypeSchema,
    title: nonEmptyTextSchema,
    titles: profileTaskSkillTitleTransportSchema.array().default([]),
});

export type ProfileTaskRequiredSkillTransport = z.infer<typeof profileTaskRequiredSkillTransportSchema>;

const profileTaskClusterTranslationTransportSchema = z.object({
    language: z.string(),
    name: nonEmptyTextSchema,
});

const profileTaskClusterTransportSchema = z.object({
    id: z.uuid(),
    name: nonEmptyTextSchema,
    translationOriginLanguage: z.string().optional(),
    translations: profileTaskClusterTranslationTransportSchema.array().optional(),
});

/**
 * A work task of a profile's TASKS section as the backend returns it: resolved into the requested
 * language, identity/order/generationState always present, content fields filling up as generation
 * progresses (absent while pending or failed).
 */
export const jobProfileTaskTransportSchema = z.object({
    // Both absent on backends without step-based assessments; state without assessment means it is on its way.
    // Display-only, so a malformed assessment hides itself rather than failing the whole task or profile read.
    assessment: profileTaskAssessmentTransportSchema.optional().catch(undefined),
    assessmentState: profileTaskAssessmentStateTransportSchema.optional().catch(undefined),
    automationRecommendationState: automationRecommendationStateFieldSchema,
    classification: profileTaskClassificationTransportSchema.optional(),
    clusters: profileTaskClusterTransportSchema.array().optional(),
    createdAt: z.string().optional(),
    description: z.string().optional(),
    estimatedEffort: profileTaskEstimatedEffortTransportSchema.optional(),
    generationState: profileTaskGenerationStateTransportSchema,
    order: z.number().int().nonnegative(),
    requiredSkills: profileTaskRequiredSkillTransportSchema.array().default([]),
    taskId: opaqueIdSchema,
    taskVersion: z.number().int().positive(),
    title: z.string().optional(),
    // Language the content was authored in; first fallback for a language absent from translations.
    translationOriginLanguage: z.string().optional(),
    // Absent on older backends; the resolved top-level fields stand in that case.
    translations: profileTaskTranslationTransportSchema.array().optional(),
    updatedAt: z.string().optional(),
    workflow: profileTaskWorkflowTransportSchema.optional(),
});

export type JobProfileTaskTransport = z.infer<typeof jobProfileTaskTransportSchema>;
